import React, { useState, useEffect } from 'react';
import './transactionHistory.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios'
import UserResponsePopup from '../shared/Popup/UserPopup';
import Select from 'react-select';
import { useAuth } from '../../context/AuthContext';

interface Transfer {
    id: string;
    datetime: string;
    amount: number;
    fromUser: string;
    toUser: string;
}

interface UserOption {
    value: string;
    label: string;
}

const TransferHistory: React.FC = () => {
    const { isLoggedIn } = useAuth();
    const [transfers, setTransfers] = useState<Transfer[]>([]);
    const [filteredTransfers, setFilteredTransfers] = useState<Transfer[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isResponsePopupOpen, setIsResponsePopupOpen] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [filterName, setFilterName] = useState('');
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [selectedOption, setSelectedOption] = useState<UserOption | null>(null);

    useEffect(() => {
        getTransfers();
    }, []);

    useEffect(() => {
        if (filterName) {
            const filtered = transfers.filter(transfer => 
                transfer.fromUser.includes(filterName) || 
                transfer.toUser.includes(filterName)
            );
            setFilteredTransfers(filtered);
        } else {
            setFilteredTransfers(transfers);
        }
    }, [filterName, transfers]);

    useEffect(() => {
        const names = new Set<string>();
        transfers.forEach(transfer => {
            names.add(transfer.fromUser);
            names.add(transfer.toUser);
        });
        
        const options = Array.from(names).sort().map(name => ({
            value: name,
            label: name
        }));
        
        setUserOptions(options);
    }, [transfers]);

    const getTransfers = async () => {
        try {
            setIsLoading(true);
            const result = await axios.get('https://kaskas.pro/split-bill-server/get-transfers');
            console.log(result);
            const transformedTransfers = result.data.map((transfer: any) => ({
                id: transfer.id,
                datetime: transfer.timestamp,
                amount: transfer.amount,
                fromUser: transfer.paid_user_name,
                toUser: transfer.received_user_name
            }));
            setTransfers(transformedTransfers);
        } catch (error) {
            console.log(error);
            setResponseMessage("אירעה שגיאה בשליפת הנתונים");
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
        } finally {
            setIsLoading(false);
        }
    }

    const onDelete = async (id: string) => {
        console.log(`Delete: ${id}`);

        try {
            setIsLoading(true);
            const result = await axios.post('https://kaskas.pro/split-bill-server/delete-transfer', { id })
            setResponseMessage("העברה נמחקה בהצלחה!");
            setIsSuccess(result.data.success);
            setIsResponsePopupOpen(true);
            await getTransfers();
        } catch (error) {
            setResponseMessage("אירעה שגיאה במחיקת העברה");
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
        } finally {
            setIsLoading(false);
        }
    }

    const formatDatetime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('he-IL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleCloseResponsePopup = () => {
        setIsResponsePopupOpen(false);
    };

    const handleSelectChange = (option: UserOption | null) => {
        setSelectedOption(option);
        setFilterName(option ? option.value : '');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterName(e.target.value);
        setSelectedOption(null);
    };

    return (
        <div className="transaction-history">
            {isLoading && (
                <div className='loading-overlay'>
                    <div className='loading-spinner'></div>
                </div>
            )}
            <h2 className="transaction-history__title">היסטוריית העברות</h2>
            
            <div className="filter-container">
                <div className="filter-input-group">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        type="text"
                        placeholder="סנן לפי שם משתמש..."
                        value={filterName}
                        onChange={handleInputChange}
                        className="filter-input"
                    />
                </div>
                
                <div className="filter-select-container">
                    <Select
                        options={userOptions}
                        value={selectedOption}
                        onChange={handleSelectChange}
                        placeholder="בחר משתמש"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
            </div>
            
            <div className="transaction-list transfer-list">
                {filteredTransfers.length > 0 ? (
                    filteredTransfers.map((transfer) => (
                        <div key={transfer.id} className="transaction-card">
                            <div className="transaction-card__header">
                                <span className="transaction-card__date">
                                    {formatDatetime(transfer.datetime)}
                                </span>
                                {isLoggedIn && (
                                    <button
                                        className="transaction-card__delete"
                                        onClick={() => onDelete(transfer.id)}
                                        aria-label="מחק"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                            <div className="transaction-card__body">
                                <div className="transaction-card__details">
                                    <div className="transaction-card__amount">
                                        ₪{transfer.amount}
                                    </div>
                                </div>
                                <div className="transaction-card__transfer">
                                    <span className="transfer-icon">💸</span>
                                    העברה מ
                                    <span className="user-name">{transfer.fromUser}</span>
                                    <span className="transfer-arrow">⟵</span>
                                    <span className="user-name">{transfer.toUser}</span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-filter-results">
                        <span className="empty-filter-icon">🔍</span>
                        <p>לא נמצאו העברות התואמות את החיפוש</p>
                    </div>
                )}
            </div>
            <UserResponsePopup
                isOpen={isResponsePopupOpen}
                isSuccess={isSuccess}
                message={responseMessage}
                onClose={handleCloseResponsePopup}
            />
        </div>
    );
};

export default TransferHistory; 